import { useCallback, useEffect, useRef, useState } from "react";
import { api, type Pantry as PantryData, type PantryItem } from "../api.ts";
import { C, mono, space, type, border } from "../theme.ts";
import { useRefresh } from "../components/PullToRefresh.tsx";
import { SectionHeader } from "../components/SectionHeader.tsx";
import { MetaLine } from "../components/MetaLine.tsx";
import { PantryRow } from "../components/PantryRow.tsx";
import { EmptyState } from "../components/EmptyState.tsx";

// The pantry. Shelf-stable staples with a tick against each; ticked means it
// is in the flat, and the unticked ones are the shopping list. That is the
// entire feature.
//
// ───────────────────────────────────────────────────────────────────────────
// Read this before adding anything to this file.
//
// Never build here: a count of how many items are missing, rendered as a badge
// or a figure anywhere — on the menu entry, at the top of a section, beside the
// toggle; a restocking history; a reminder; any colour that means *overdue*. A
// list of items is a list. A number that stands for how far behind the cupboard
// is, is a verdict, and this is the same rule slots and routines run under:
//
//     The system may know where you are.
//     It may never tell you how you're doing.
//
// Ticking is the only interaction. No dates, no quantities, no prices.
//
// ── Why there is a queue in here, when the service worker refuses to have one
//
// public/sw.js never replays a write, and the reason it gives is right: a write
// that silently happens later is worse than one that plainly did not happen.
// This view is the one place that would be unusable under that rule — it is
// read and ticked in a supermarket, which is the worst signal in the day, and a
// tick that bounces is the whole feature failing at exactly the moment it is
// wanted.
//
// So the replay lives here rather than in the worker, and the difference is
// that it is not silent. An unsent tick is shown as unsent, on its own row,
// until it lands; it survives the app being closed; and it is retried on
// reopening and on the signal coming back. Nothing about it is background. The
// worker's rule holds everywhere else, unchanged.
// ───────────────────────────────────────────────────────────────────────────

const KEY = "mc.pantry";

/** Ticks made on this device that have not reached the server yet. */
type Pending = Record<string, boolean>;

interface Cached { sections: PantryData["sections"]; pending: Pending }

function read(): Cached {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? "null");
    if (!raw || !Array.isArray(raw.sections)) return { sections: [], pending: {} };
    return { sections: raw.sections, pending: raw.pending ?? {} };
  } catch {
    return { sections: [], pending: {} };
  }
}

function write(next: Cached) {
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // A browser refusing storage means the list is online-only. The ticks that
    // did go through are on the server; the ones that did not are on screen as
    // unsent until the tab closes.
  }
}

/**
 * The list with one item's tick settled into it.
 *
 * This is the counterpart to dropping an id from `pending`. The two have to
 * happen together: `pending` is only an overlay on top of `in_stock`, so
 * lifting the overlay without moving what is underneath falls straight back to
 * the value this device last fetched.
 */
function settle(sections: PantryData["sections"], id: string, in_stock: boolean) {
  return sections.map((s) => ({
    ...s,
    items: s.items.map((i) => (i.id === id ? { ...i, in_stock } : i)),
  }));
}

type Showing = "all" | "to_buy";

export function Pantry() {
  const [sections, setSections] = useState<PantryData["sections"]>(() => read().sections);
  const [pending, setPending] = useState<Pending>(() => read().pending);
  const [showing, setShowing] = useState<Showing>("all");
  // Set when the list on screen is the last one this device saw rather than
  // one just fetched. Said plainly rather than hidden — offline is a normal
  // state of a phone in a supermarket, not an error.
  const [offline, setOffline] = useState(false);
  const [loaded, setLoaded] = useState(() => read().sections.length > 0);
  // Ticks the server refused, against the row they were made on. Not cached:
  // a refusal is about one attempt, and it is answered by ticking again, not
  // by being carried into tomorrow.
  const [refused, setRefused] = useState<Record<string, string>>({});
  const flushing = useRef(false);

  // One place that writes the cache, so the two halves cannot drift apart.
  const store = useCallback((s: PantryData["sections"], p: Pending) => {
    setSections(s); setPending(p); write({ sections: s, pending: p });
  }, []);

  /**
   * Send the ticks this device is holding. One at a time and in insertion
   * order: this is a handful of rows, not a batch, and a failure part-way
   * through should leave the rest still queued rather than half-applied under
   * one status code.
   *
   * Two ways out of the queue, and they are not the same thing. A tick that
   * lands is settled into the list. A tick the server refuses — a 4xx, an
   * answer rather than a silence — comes off and says so on its row, because
   * retrying it forever would never produce a different answer. Anything else,
   * including no response at all, is the signal and stays queued.
   */
  const flush = useCallback(async () => {
    if (flushing.current) return;
    flushing.current = true;
    try {
      for (const [id, value] of Object.entries(read().pending)) {
        let landed = true;
        try {
          await api.setPantryItem(id, value);
        } catch (e: any) {
          const status = e?.status;
          const answered = typeof status === "number" && status >= 400 && status < 500;
          if (!answered) return;   // still no signal; keep this and the rest
          landed = false;
          setRefused((r) => ({
            ...r,
            [id]: status === 404 ? "no longer on the list" : "not saved",
          }));
        }
        // Re-read rather than trusting what this loop started with. A tick
        // made in the shop while the previous one was in flight is newer than
        // the value just sent, and dropping it here would un-tick a row that
        // is visibly ticked.
        const now = read();
        if (!(id in now.pending) || now.pending[id] !== value) continue;
        const held = { ...now.pending };
        delete held[id];
        // The value has to go into the list as the queue entry leaves it.
        // Removing the entry on its own was the whole of the bug: the row
        // ticked on the tap and then quietly un-ticked itself a moment later,
        // the instant the write succeeded, falling back to the `in_stock`
        // fetched on mount. A refused tick is the case where falling back is
        // the correct thing, so the list is left alone.
        const next = landed ? settle(now.sections, id, value) : now.sections;
        write({ sections: next, pending: held });
        setSections(next); setPending(held);
      }
    } finally {
      flushing.current = false;
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const fresh = await api.pantry();
      // Whatever this device has not managed to send yet is still the truth on
      // this device, so it stays on top of the server's answer rather than
      // being overwritten by it and appearing to un-tick itself in the shop.
      store(fresh.sections, read().pending);
      // A list straight from the server answers every refusal on screen.
      setRefused({});
      setOffline(false); setLoaded(true);
      await flush();
    } catch {
      // The cached list is already on screen. Say so, and keep the ticks.
      setOffline(true);
    }
  }, [store, flush]);

  useEffect(() => { load(); }, [load]);

  // The pull re-reads the list. It is emphatically not how a tick appears —
  // that happens on the tap, above, and if it ever stops doing so this is not
  // the place to fix it.
  useRefresh(load);

  // The signal coming back, and the app being reopened. Both are the moment to
  // try again; neither is a timer, because a retry loop running in a pocket is
  // a background write by another name.
  useEffect(() => {
    const again = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("online", again);
    document.addEventListener("visibilitychange", again);
    return () => {
      window.removeEventListener("online", again);
      document.removeEventListener("visibilitychange", again);
    };
  }, [load]);

  const toggle = (item: PantryItem) => {
    const value = !(item.id in pending ? pending[item.id] : item.in_stock);
    const next = { ...pending, [item.id]: value };
    // Ticking again is the reply to a refusal, so the message goes.
    if (item.id in refused) {
      setRefused((r) => { const rest = { ...r }; delete rest[item.id]; return rest; });
    }
    store(sections, next);
    flush();
  };

  const ticked = (item: PantryItem) => (item.id in pending ? pending[item.id] : item.in_stock);

  const visible = sections
    .map((s) => ({ ...s, items: showing === "all" ? s.items : s.items.filter((i) => !ticked(i)) }))
    .filter((s) => s.items.length > 0);

  return (
    <div style={{ padding: `0 ${space.xxl}px` }}>
      {/* Two views of one list. Named, never numbered: a figure beside "to buy"
          would be the count this feature is written without. */}
      <div style={{ display: "flex", gap: space.lg, alignItems: "baseline", marginBottom: space.xs }}>
        {([["all", "EVERYTHING"], ["to_buy", "TO BUY"]] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setShowing(key)}
            style={{
              fontFamily: mono, fontSize: type.small, letterSpacing: 1,
              color: showing === key ? C.inkSoft : C.inkGhost,
              background: "none", border: "none", padding: 0, cursor: "pointer",
              borderBottom: `${border.hairline}px solid ${showing === key ? C.ruleStrong : "transparent"}`,
            }}
          >{label}</button>
        ))}
      </div>

      {offline && loaded && (
        <MetaLine style={{ margin: `${space.md}px 0 0` }}>
          The last copy from when you had a signal. Ticking still works.
        </MetaLine>
      )}

      {!loaded && !offline && <EmptyState>Looking…</EmptyState>}
      {!loaded && offline && <EmptyState>No signal, and this phone has not seen the list yet.</EmptyState>}

      {loaded && showing === "to_buy" && visible.length === 0 && (
        <EmptyState>Everything on the list is in the flat.</EmptyState>
      )}

      {visible.map((section, i) => (
        <div key={section.name}>
          <SectionHeader style={{ margin: i === 0 ? `${space.xxl}px 0 ${space.sm}px` : `${space.huge}px 0 ${space.sm}px` }}>
            {section.name.toUpperCase()}
          </SectionHeader>

          {section.items.map((item) => {
            const on = ticked(item);
            const unsent = item.id in pending;
            // Said on the row it belongs to, not totalled at the top: this one
            // has not reached the server, or came back off because the server
            // said no, and either way it is answered by ticking again.
            const tag = unsent ? "not sent yet" : item.id in refused ? refused[item.id] : null;
            return <PantryRow key={item.id} item={item} on={on} tag={tag} onToggle={() => toggle(item)} />;
          })}
        </div>
      ))}
    </div>
  );
}
